import React, { useEffect, useState } from 'react';
/* eslint-disable import/first */
// ⚠️ CRITICAL: storageGuard MUST be imported FIRST — it patches localStorage globally
// to prevent iOS 5MB quota crashes by auto-routing large values to IndexedDB.
import './utils/storageGuard';
// 📡 Auto-mirror UI state (tabs, progress, preferences) to MongoDB so reinstall preserves state.
import { installUserStateSync } from './utils/userStateSync';
installUserStateSync();
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import '@/App.css';
import { SessionProvider, useSession } from './context/SessionContext';
import { auth, signOut } from './config/firebase';
import { trackEvent, trackPageView } from './services/analytics';
import { restoreCriticalFlags, setPersistent } from './utils/persistentStorage';

import { isFeatureUnlocked, initializeStore } from './services/iapService';
import useKeyboardOpen from './hooks/useKeyboardOpen';


// FRESH START v18 - NUCLEAR RESET (Feb 2026)
// Full wipe of ALL user data on every device. No preserves.
// Every device behaves like first-ever install on next open.
const FRESH_START_VERSION = 'v20_production_clean_slate_feb2026';
const NUCLEAR_VERSIONS = new Set(['v18_nuclear_reset_feb2026', 'v19_nuclear_reset_feb2026_v2', 'v20_production_clean_slate_feb2026']);

// Wrap in try-catch to prevent any crash during module load
try {
  const lastFreshStart = localStorage.getItem('fresh_start_version');
  const isNuclear = NUCLEAR_VERSIONS.has(FRESH_START_VERSION);

  // Only clear if this is a NEW version (not just a tab reopen)
  if (lastFreshStart !== FRESH_START_VERSION && lastFreshStart !== null) {
    console.log(`🧹 FRESH START ${FRESH_START_VERSION} - ${isNuclear ? 'NUCLEAR WIPE' : 'VERSION UPGRADE'}`);

    // Nuclear wipe path: preserve NOTHING. User becomes first-time user.
    if (isNuclear) {
      localStorage.clear();
      sessionStorage.clear();

      // Wipe Capacitor Preferences (native SharedPreferences/UserDefaults)
      try {
        if (window.Capacitor?.isNativePlatform?.()) {
          import('@capacitor/preferences')
            .then(({ Preferences }) => Preferences.clear())
            .catch(() => {});
        }
      } catch (_) {}

      // Wipe IndexedDB
      if (window.indexedDB && typeof indexedDB.databases === 'function') {
        indexedDB.databases().then((dbs) => {
          (dbs || []).forEach((dbInfo) => {
            try { indexedDB.deleteDatabase(dbInfo.name); } catch (_) {}
          });
        }).catch(() => {});
      }

      console.log('☢️  NUCLEAR WIPE complete — device reset to first-install state');
    } else {
      // Legacy soft-upgrade path (kept for future non-nuclear bumps)
      const watchingStory = localStorage.getItem('watching_story');
      const unifiedStoryData = localStorage.getItem('unified_story_data');
      const birthDetails = localStorage.getItem('birthDetails');
      const birthDetailsAlt = localStorage.getItem('birth_details');
      const isAuth = localStorage.getItem('isAuthenticated');
      const userId = localStorage.getItem('user_id');
      const sessionId = localStorage.getItem('session_id');
      const isPremium = localStorage.getItem('is_premium');
      const isPremiumAlt = localStorage.getItem('isPremium');
      const deviceId = localStorage.getItem('device_id');
      const taraanayaDeviceId = localStorage.getItem('taraanaya_device_id');
      const formDraft = localStorage.getItem('onboarding_form_draft');
      const onboardingInProgress = localStorage.getItem('onboarding_in_progress');
      const storyGenInProgress = localStorage.getItem('story_generation_in_progress');
      const onboardingCompleted = localStorage.getItem('onboarding_completed');
      const aiStoryData = localStorage.getItem('ai_story_data');
      const psLanguage = localStorage.getItem('ps_selected_language');
      const psDomain = localStorage.getItem('ps_selected_domain');
      const psMahadasha = localStorage.getItem('ps_selected_mahadasha');
      const pastStoryViewed = localStorage.getItem('past_story_viewed');
      const freeStoryUsed = localStorage.getItem('free_story_used');

      localStorage.clear();
      sessionStorage.clear();

      if (watchingStory === 'true' && (unifiedStoryData || birthDetails || birthDetailsAlt)) {
        localStorage.setItem('watching_story', watchingStory);
        if (unifiedStoryData) localStorage.setItem('unified_story_data', unifiedStoryData);
      }
      if (birthDetails) localStorage.setItem('birthDetails', birthDetails);
      if (birthDetailsAlt) localStorage.setItem('birth_details', birthDetailsAlt);
      if (isAuth) localStorage.setItem('isAuthenticated', isAuth);
      if (userId) localStorage.setItem('user_id', userId);
      if (sessionId) localStorage.setItem('session_id', sessionId);
      if (isPremium) localStorage.setItem('is_premium', isPremium);
      if (isPremiumAlt) localStorage.setItem('isPremium', isPremiumAlt);
      if (deviceId) localStorage.setItem('device_id', deviceId);
      if (taraanayaDeviceId) localStorage.setItem('taraanaya_device_id', taraanayaDeviceId);
      if (formDraft) localStorage.setItem('onboarding_form_draft', formDraft);
      if (onboardingInProgress) localStorage.setItem('onboarding_in_progress', onboardingInProgress);
      if (storyGenInProgress) localStorage.setItem('story_generation_in_progress', storyGenInProgress);
      if (onboardingCompleted) localStorage.setItem('onboarding_completed', onboardingCompleted);
      if (aiStoryData) localStorage.setItem('ai_story_data', aiStoryData);
      if (psLanguage) localStorage.setItem('ps_selected_language', psLanguage);
      if (psDomain) localStorage.setItem('ps_selected_domain', psDomain);
      if (psMahadasha) localStorage.setItem('ps_selected_mahadasha', psMahadasha);
      if (pastStoryViewed) localStorage.setItem('past_story_viewed', pastStoryViewed);
      if (freeStoryUsed) localStorage.setItem('free_story_used', freeStoryUsed);
    }
    
    // Clear IndexedDB if exists
    if (window.indexedDB) {
      indexedDB.databases().then(dbs => {
        dbs.forEach(db => {
          indexedDB.deleteDatabase(db.name);
          console.log('🗑️ Deleted IndexedDB:', db.name);
        });
      }).catch(() => {});
    }
    
    // Mark complete
    localStorage.setItem('fresh_start_version', FRESH_START_VERSION);
    if (isNuclear) {
      // Tell restoreCriticalFlags to skip this boot — Preferences was wiped too
      window.__SKIP_RESTORE_CRITICAL_FLAGS = true;
    }
    
    console.log('✅ ALL app data cleared - COMPLETE FRESH START');
  } else if (lastFreshStart === null) {
    // First time user - just set the version
    localStorage.setItem('fresh_start_version', FRESH_START_VERSION);
    console.log('🆕 First time user - version set');
  }
} catch (e) {
  console.log('FRESH_START skipped:', e.message);
}

// Restore critical flags from Capacitor Preferences (survives localStorage eviction)
// Non-blocking: runs in background on app startup.
// Skipped right after a nuclear wipe so Preferences.clear() can finish first.
if (!window.__SKIP_RESTORE_CRITICAL_FLAGS) {
  restoreCriticalFlags().catch(() => {});
}

// Global function to manually clear all data (can be called from console)
// NOTE: Preserves device-level flags AND story session flags
window.clearAllAppData = async () => {
  console.log('🧹 Manually clearing ALL app data (preserving important flags)...');
  
  // Preserve device-level flags
  const deviceId = localStorage.getItem('device_id');
  const taraanayaDeviceId = localStorage.getItem('taraanaya_device_id');
  const pastStoryViewed = localStorage.getItem('past_story_viewed');
  const freeStoryUsed = localStorage.getItem('free_story_used');
  
  // Preserve story session flags (for resume functionality)
  const watchingStory = localStorage.getItem('watching_story');
  const unifiedStoryData = localStorage.getItem('unified_story_data');
  const birthDetails = localStorage.getItem('birthDetails');
  const birthDetailsAlt = localStorage.getItem('birth_details');
  const audioUserId = localStorage.getItem('audio_user_id');
  const sessionId = localStorage.getItem('session_id');
  
  // Clear storage
  localStorage.clear();
  sessionStorage.clear();
  
  // Restore device-level flags
  if (deviceId) localStorage.setItem('device_id', deviceId);
  if (taraanayaDeviceId) localStorage.setItem('taraanaya_device_id', taraanayaDeviceId);
  if (pastStoryViewed) localStorage.setItem('past_story_viewed', pastStoryViewed);
  if (freeStoryUsed) localStorage.setItem('free_story_used', freeStoryUsed);
  
  // Restore story session flags
  if (watchingStory) localStorage.setItem('watching_story', watchingStory);
  if (unifiedStoryData) localStorage.setItem('unified_story_data', unifiedStoryData);
  if (birthDetails) localStorage.setItem('birthDetails', birthDetails);
  if (birthDetailsAlt) localStorage.setItem('birth_details', birthDetailsAlt);
  if (audioUserId) localStorage.setItem('audio_user_id', audioUserId);
  if (sessionId) localStorage.setItem('session_id', sessionId);
  
  // Clear cookies
  document.cookie.split(";").forEach((c) => {
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
  
  // Clear IndexedDB
  if (window.indexedDB) {
    try {
      const databases = await window.indexedDB.databases();
      for (const db of databases) {
        if (db.name) {
          window.indexedDB.deleteDatabase(db.name);
          console.log('Deleted:', db.name);
        }
      }
    } catch (e) {}
  }
  
  // Clear Cache Storage
  if ('caches' in window) {
    try {
      const names = await caches.keys();
      for (const name of names) {
        await caches.delete(name);
        console.log('Deleted cache:', name);
      }
    } catch (e) {}
  }
  
  // Sign out Firebase
  try {
    await signOut(auth);
  } catch (e) {}
  
  console.log('✅ All data cleared (device-level flags preserved). Reloading...');
  window.location.reload();
};

// COMPLETE FRESH START - clears EVERYTHING including device ID
window.completeFreshStart = async () => {
  console.log('🔥 COMPLETE FRESH START - Clearing EVERYTHING including device ID...');
  
  // Clear ALL storage - no preservation
  localStorage.clear();
  sessionStorage.clear();
  
  // Clear cookies
  document.cookie.split(";").forEach((c) => {
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
  
  // Clear IndexedDB
  if (window.indexedDB) {
    try {
      const databases = await window.indexedDB.databases();
      for (const db of databases) {
        if (db.name) {
          window.indexedDB.deleteDatabase(db.name);
        }
      }
    } catch (e) {}
  }
  
  // Clear Cache Storage
  if ('caches' in window) {
    try {
      const names = await caches.keys();
      for (const name of names) {
        await caches.delete(name);
      }
    } catch (e) {}
  }
  
  // Sign out Firebase
  try {
    await signOut(auth);
  } catch (e) {}
  
  console.log('✅ COMPLETE FRESH START done! Reloading...');
  window.location.reload();
};

// Check if user has used their FREE past story view on this device
const hasUsedFreeStory = () => {
  return localStorage.getItem('free_story_used') === 'true';
};

// Mark that user has used their free story (called after first story view)
const markFreeStoryUsed = () => {
  setPersistent('free_story_used', 'true');
  console.log('🔒 Free story used - future content now locked');
};

// Check if user is logged in (ONLY Firebase-authenticated users)
const isLoggedIn = () => {
  // Only Firebase-authenticated users count as "logged in"
  if (localStorage.getItem('isAuthenticated') === 'true' && localStorage.getItem('user_id') !== null) {
    return true;
  }
  return false;
};

// Protected Route - Requires login (only after free story is used)
const ProtectedRoute = ({ children }) => {
  // If user hasn't used free story yet, let them through
  if (!hasUsedFreeStory()) {
    return children;
  }
  // After free story is used, require login
  if (!isLoggedIn()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Premium Route - Requires premium subscription for access
// Used for Future Reels, Life Insights, Timelines after payment
const PremiumRoute = ({ children, fallbackUrl = '/payment' }) => {
  const { isPremium, isLoading } = useSession();
  
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a1929 0%, #1a2f45 100%)'
      }}>
        <div style={{ color: '#14b8a6', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }
  
  if (!isPremium) {
    return <Navigate to={fallbackUrl} replace />;
  }
  
  return children;
};

// Feature-gated route - checks specific feature unlock
const FeatureRoute = ({ children, feature, fallbackUrl = '/payment' }) => {
  const { isPremium, isLoading } = useSession();
  
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a1929 0%, #1a2f45 100%)'
      }}>
        <div style={{ color: '#14b8a6', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }
  
  if (!isPremium || !isFeatureUnlocked(feature)) {
    return <Navigate to={fallbackUrl} replace />;
  }
  
  return children;
};

// Free Story Gate - Allows ONE free view per device
// Only checks on initial mount, not on re-renders
const FreeStoryGate = ({ children }) => {
  const [allowed, setAllowed] = React.useState(null);
  
  React.useEffect(() => {
    // Landing page visitors always get access
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('source') === 'landing') {
      localStorage.setItem('watching_story', 'true');
      setAllowed(true);
      return;
    }

    // Check once on mount
    if (isLoggedIn()) {
      setAllowed(true);
    } else {
      // Check if past story already viewed on this device
      const pastStoryViewed = localStorage.getItem('past_story_viewed') === 'true' || 
                              localStorage.getItem('free_story_used') === 'true';
      
      if (pastStoryViewed && !localStorage.getItem('watching_story')) {
        // Past story used AND not currently watching - redirect to login
        setAllowed(false);
      } else {
        // First time OR currently watching - allow
        localStorage.setItem('watching_story', 'true');
        setAllowed(true);
      }
    }
  }, []);
  
  if (allowed === null) {
    return null; // Loading
  }
  
  if (!allowed) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// /watch route resolver — context-aware so the user is never sent back
// to the marketing landing once they've moved forward.
//   • Logged-in user                       → /dashboard
//   • Past story viewed (free story used)  → /login
//   • Cached story + birth details mid-flow → /loading-past-reels?source=landing
//   • Fresh visitor                        → marketing landing (WebsiteLandingPage)
const WatchRouteResolver = () => {
  const [decision, setDecision] = React.useState(null); // 'landing' | 'redirect:<path>'

  React.useEffect(() => {
    // 1) Already logged in → straight to dashboard
    if (isLoggedIn()) {
      setDecision('redirect:/dashboard');
      return;
    }
    // 2) Free story already consumed → must log in to continue
    const pastStoryViewed = localStorage.getItem('past_story_viewed') === 'true' ||
                            localStorage.getItem('free_story_used') === 'true';
    if (pastStoryViewed) {
      setDecision('redirect:/login');
      return;
    }
    // 3) Mid-flow (cached story + birth details, not yet viewed) → resume planets
    const cachedStory = localStorage.getItem('unified_story_data');
    const cachedBirth = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
    if (cachedStory && cachedBirth) {
      localStorage.setItem('watching_story', 'true');
      setDecision('redirect:/loading-past-reels?source=landing');
      return;
    }
    // 4) Fresh visitor → show the marketing landing
    setDecision('landing');
  }, []);

  if (decision === null) return null; // wait one tick — avoid flicker
  if (decision === 'landing') return <WebsiteLandingPage />;
  return <Navigate to={decision.slice('redirect:'.length)} replace />;
};

// App Entry - Handles the initial routing logic
// EDGE CASES:
// 1. On Past Story → app closes → reopen = Resume Past Story
// 2. On Onboarding → app closes → reopen = Resume Onboarding
// 3. Past Story viewed → on Login → app closes = Open Login
// 4. Logged in → app closes = Open Dashboard
// 5. FRESH iOS install (even with residual flags) → Onboarding
const AppEntry = () => {
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(true);
  // Globally track soft-keyboard state. Sets `html.kb-open` class + `--kb-h` CSS var.
  // Any fixed bottom bar tagged `data-kb-hide` will auto-slide-down on Android/iOS/Web.
  useKeyboardOpen();
  
  // Track app open/session start
  useEffect(() => {
    const source = new URLSearchParams(window.location.search).get('source') || 'direct';
    const utmSource = new URLSearchParams(window.location.search).get('utm_source') || '';
    trackEvent('app_open', { 
      source, 
      utm_source: utmSource, 
      path: window.location.pathname,
      is_returning_user: !!localStorage.getItem('user_id')
    });
    // Eagerly initialize the native IAP store so the Credits Refill modal
    // (opened directly from Dashboard) finds the store ready. Without this
    // eager init, only PaymentPlans.jsx triggered init — so credit refill
    // from Dashboard failed with a 5s "Google Play is still loading" timeout.
    // Non-blocking — runs in background.
    initializeStore().catch(() => {});

    // Refresh native StoreKit / Play Billing prices whenever the app returns
    // from background. Without this, cached prices go stale after the user
    // changes App Store country or after we change pricing tiers in App Store
    // Connect — leading to "$1.99 in app, ₹249 charged by Apple" mismatches.
    let appListenerHandle = null;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App: CapApp } = await import('@capacitor/app');
        const { refreshStorePrices } = await import('./services/iapService');
        appListenerHandle = await CapApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            refreshStorePrices().catch(() => {});
          }
        });
      } catch (_) {
        // @capacitor/app not available (web) — silently ignore.
      }
    })();
    return () => {
      try { appListenerHandle?.remove(); } catch {}
    };
  }, []);
  
  useEffect(() => {
    // ⚡ OPTIMIZATION: Fire the MongoDB sync IMMEDIATELY on mount (parallel with splash timer),
    // not sequentially AFTER splash ends. This saves 500ms-2s per app open.
    // Race: wait for min splash duration (2s) OR sync complete + hard cap at 4s for cold-start networks.
    const MIN_SPLASH_MS = 2000;  // minimum brand display time (was 3000)
    const MAX_SPLASH_MS = 4000;  // hard cap even if sync hangs
    const mountAt = Date.now();

    const syncPromise = (async () => {
      try {
        const { getDeviceId, syncStoryStateFromServer } = await import('./utils/storyState');
        await getDeviceId();
        // Race sync against a 3s timeout — don't let a slow Railway cold-start block UX
        await Promise.race([
          syncStoryStateFromServer(),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      } catch (e) {
        console.warn('[AppEntry] sync error:', e);
      }
    })();

    const checkAndNavigate = async () => {
      // Check for reset parameter - clears all localStorage for fresh start
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('reset') === 'true') {
        console.log('🗑️ RESET REQUESTED - Clearing all localStorage...');
        const deviceId = localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id');
        localStorage.clear();
        if (deviceId) {
          localStorage.setItem('taraanaya_device_id', deviceId);
          localStorage.setItem('device_id', deviceId);
        }
        console.log('✅ localStorage cleared! Redirecting to fresh onboarding...');
        window.location.href = window.location.origin + '/onboarding-new';
        return;
      }

      // Check all state flags
      const hasLocalAuth = localStorage.getItem('isAuthenticated') === 'true';
      const hasUserId = localStorage.getItem('user_id');
      const hasPastStoryViewed = localStorage.getItem('past_story_viewed') === 'true' || 
                                  localStorage.getItem('free_story_used') === 'true';
      const wasWatchingStory = localStorage.getItem('watching_story') === 'true';
      const wasOnboarding = localStorage.getItem('onboarding_in_progress') === 'true';
      const hasBirthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      const hasUnifiedStoryData = localStorage.getItem('unified_story_data');
      const hasCompletedOnboarding = localStorage.getItem('onboarding_completed') === 'true';
      const wasGeneratingStory = localStorage.getItem('story_generation_in_progress') === 'true' || 
                                  sessionStorage.getItem('story_generation_in_progress') === 'true';
      
      console.log('App Entry:', { 
        hasLocalAuth, hasUserId, hasPastStoryViewed, 
        wasWatchingStory, wasOnboarding, hasBirthDetails: !!hasBirthDetails,
        hasUnifiedStoryData: !!hasUnifiedStoryData, hasCompletedOnboarding, wasGeneratingStory
      });
      
      // Get or create device_id (always needed)
      let deviceId = localStorage.getItem('taraanaya_device_id');
      if (!deviceId) {
        deviceId = localStorage.getItem('device_id');
      }
      if (!deviceId) {
        deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('taraanaya_device_id', deviceId);
        localStorage.setItem('device_id', deviceId);
      }
      
      // PRIORITY 1: Logged in user → Dashboard
      if (isLoggedIn()) {
        console.log('➡️ User logged in → Dashboard');
        navigate('/dashboard', { replace: true });
        return;
      }
      
      // PRIORITY 1.5: Returning user (device previously linked to an account) → /login
      // This covers app uninstall+reinstall, localStorage clear, cache clear, etc.
      // MongoDB syncStoryStateFromServer() has already hydrated `returning_user` flag
      // if this device had an account before.
      const isReturningUser = localStorage.getItem('returning_user') === 'true';
      const deviceLinkedEmail = localStorage.getItem('device_linked_email');
      if (isReturningUser) {
        console.log('👋 Returning user detected (linked to account) → /login (prefilled)', deviceLinkedEmail);
        navigate('/login', { replace: true });
        return;
      }
      
      // PRIORITY 2: Was watching Past Story (interrupted) → Resume on Planets dashboard
      // User explicitly prefers planets dashboard over individual domain page on reopen.
      // From there they can tap a planet to continue where they left off.
      // DON'T redirect if user is already on generating-story or loading-past-reels
      const currentPath = window.location.pathname;
      if (wasWatchingStory && !hasPastStoryViewed && currentPath !== '/generating-story' && currentPath !== '/loading-past-reels') {
        console.log('Was watching story -> Resume on Planets at /loading-past-reels');
        // Clear the flag so subsequent navigation is clean
        localStorage.removeItem('watching_story');
        navigate('/loading-past-reels', { replace: true });
        return;
      }
      
      // PRIORITY 2.5: Has story data but hasn't viewed → Resume on Planets dashboard
      // (user should pick which domain to watch; they hadn't committed to any domain yet)
      // DON'T redirect if user is already on generating-story or loading-past-reels
      if (hasUnifiedStoryData && !hasPastStoryViewed && currentPath !== '/generating-story' && currentPath !== '/loading-past-reels') {
        console.log('Has story data, not viewed -> Resume on Planets at /loading-past-reels');
        navigate('/loading-past-reels', { replace: true });
        return;
      }
      
      // PRIORITY 2.7: Was generating story (app killed during generation) → Resume generation
      // This catches the case where user submitted birth details, story was generating, then app was killed
      if (wasGeneratingStory && hasBirthDetails && !hasCompletedOnboarding && !hasPastStoryViewed) {
        console.log('Story generation was interrupted -> Resume at /generating-story');
        navigate('/generating-story', { replace: true });
        return;
      }
      
      // PRIORITY 3: Past Story viewed (legitimate returning user) → Login (ALWAYS)
      // Rule: "Past story once viewed never shows again." On both web and native, once the
      // user has explicitly pressed Skip / Continue / Login / Download, the next app-open
      // lands on /login (or /dashboard if they complete auth). They NEVER see the past-story
      // planets or a cached reel again.
      if (hasPastStoryViewed) {
        console.log('➡️ Past story viewed → Login');
        navigate('/login', { replace: true });
        return;
      }
      
      // PRIORITY 4: Has birth details but hasn't completed onboarding → Resume Onboarding
      if ((wasOnboarding || hasBirthDetails) && !hasCompletedOnboarding) {
        console.log('➡️ Was on onboarding/has birth details → Resume Onboarding');
        navigate('/onboarding-new', { replace: true });
        return;
      }
      
      // PRIORITY 5: Fresh user → Onboarding
      // Only for users who have NEVER viewed past story
      console.log('✨ Fresh user → Onboarding');
      
      // Don't await signOut - it hangs on iOS for fresh users
      // Just fire-and-forget since user isn't signed in anyway
      console.log('🔑 [AppEntry] Triggering signOut (fire-and-forget)...');
      signOut(auth).catch(e => console.log('🔑 signOut background error:', e?.message));
      
      console.log('🚀 [AppEntry] Navigating to /onboarding-new NOW');
      navigate('/onboarding-new', { replace: true });
      console.log('🚀 [AppEntry] Navigate called successfully');
    };
    
    // ⚡ Navigate as soon as EITHER the MIN splash elapsed AND sync done, OR at MAX cap.
    // This gives ~2s min brand feel but opens app instantly on fast connections.
    let navigated = false;
    const doNavigate = () => {
      if (navigated) return;
      navigated = true;
      const elapsed = Date.now() - mountAt;
      console.log(`⏰ [AppEntry] Hiding splash after ${elapsed}ms, navigating...`);
      setShowSplash(false);
      checkAndNavigate().catch(err => {
        console.error('❌ [AppEntry] checkAndNavigate error:', err);
        navigate('/onboarding-new', { replace: true });
      });
    };

    // Path A: sync finishes → wait for MIN splash → go
    syncPromise.then(() => {
      const elapsed = Date.now() - mountAt;
      const wait = Math.max(0, MIN_SPLASH_MS - elapsed);
      setTimeout(doNavigate, wait);
    });

    // Path B: hard timeout → navigate regardless
    const hardCap = setTimeout(doNavigate, MAX_SPLASH_MS);
    
    return () => clearTimeout(hardCap);
  }, [navigate]);
  
  if (showSplash) {
    return <SplashPerfection />;
  }
  
  return null;
};

// New Redesigned Pages
import SplashNew from './pages/SplashNew';
import NameEntryNew from './pages/NameEntryNew';
import DateOfBirthNew from './pages/DateOfBirthNew';
import TimeOfBirthNew from './pages/TimeOfBirthNew';
import PlaceOfBirthNew from './pages/PlaceOfBirthNew';
import OTPVerificationNew from './pages/OTPVerificationNew';
import LoadingStory from './pages/LoadingStory';
import LoadingStoryGenerator from './pages/LoadingStoryGenerator';
import PastStoryNew from './pages/PastStoryNew';
import PastStoryLanding from './pages/PastStoryLanding';
import LandingPageMarketing from './pages/LandingPageMarketing';
import WebsiteLandingPage from './pages/WebsiteLandingPage';
import WebsiteAboutPage from './pages/WebsiteAboutPage';
import PaymentNew from './pages/PaymentNew';
import HomeNew from './pages/HomeNew';
import HomeInnovative from './pages/HomeInnovative';
import MyDomainsNew from './pages/MyDomainsNew';
import DashboardNew from './pages/DashboardNew';
import StoryVideoNew from './pages/StoryVideoNew';
import ProfileNew from './pages/ProfileNew';
import DomainDetailUniversal from './pages/DomainDetailUniversal';
import AIAgentsHub from './pages/AIAgentsHub';
import AuthCallback from './pages/AuthCallback';
import CreditsRefill from './pages/CreditsRefill';

// Phase 4: Minimal Intelligence Framework
import HomeMinimal from './pages/minimal/HomeMinimal';
import HealthMinimal from './pages/minimal/HealthMinimal';
import HomeSimple from './pages/HomeSimple';
import OnboardingRevolutionary from './pages/OnboardingRevolutionary';
import SplashRevolutionary from './pages/SplashRevolutionary';
import SplashPerfect from './pages/SplashPerfect';
import SplashCinematic from './pages/SplashCinematic';
import SplashClassic from './pages/SplashClassic';
import SplashFlawless from './pages/SplashFlawless';
import SplashFinal from './pages/SplashFinal';
import SplashUltimate from './pages/SplashUltimate';
import SplashModern from './pages/SplashModern';
import SplashWorld from './pages/SplashWorld';
import SplashCosmic from './pages/SplashCosmic';
import SplashPerfection from './pages/SplashPerfection';
import SmartNotebookInput from './pages/SmartNotebookInput';
import PastStoryUnified from './pages/PastStoryUnified';
import PastStoryUnifiedCinematic from './pages/PastStoryUnifiedCinematic';
import PastStoryRevolutionary from './pages/PastStoryRevolutionary';
import PastStoryAnimated from './pages/PastStoryAnimated';
import FutureStoryRevolutionary from './pages/FutureStoryRevolutionary';
import FutureStoryDetailed from './pages/FutureStoryDetailed';
import LoginFirebase from './pages/LoginFirebase';  // Firebase Phone OTP (Production)
import BeautifulDashboard from './pages/BeautifulDashboard';
import DomainStoryFuture from './pages/DomainStoryFuture';
import CareerInsights from './pages/CareerInsights';
import DomainReelsStory from './pages/DomainReelsStory';
import FutureStoryUnified from './pages/FutureStoryUnified';
import FutureStoryExact from './pages/FutureStoryExact';  // NEW: Exact copy of past story design
import HexStory from './pages/HexStory';
import LoadingDomainReel from './pages/LoadingDomainReel';
import LoadingPastStoryReels from './pages/LoadingPastStoryReels';
import FutureReelsLoading from './pages/FutureReelsLoading';
import ValidationResults from './pages/ValidationResults';
import CompleteValidation from './pages/CompleteValidation';
import Complete100Analysis from './pages/Complete100Analysis';
import AlgorithmValidation from './pages/AlgorithmValidation';
import ExactPrediction from './pages/ExactPrediction';
import HealthStoryFlow from './pages/HealthStoryFlow';
import BulkKernelGenerator from './pages/BulkKernelGenerator';
import CompletePredictions from './pages/CompletePredictions';
import MandeepHealthDebug from './pages/MandeepHealthDebug';
import DomainInsights from './pages/DomainInsights';
import AuditTrailReport from './pages/AuditTrailReport';
import SocialMediaContent from './pages/SocialMediaContent';
import CelebrityDatasetDownload from './pages/CelebrityDatasetDownload';
import BrandMaterials from './pages/BrandMaterials';

// Old Pages (kept for reference)
import ZodiacSplash from './pages/ZodiacSplash';
import NameEntry from './pages/NameEntry';
import PlaceOfBirth from './pages/PlaceOfBirth';
import DateOfBirth from './pages/DateOfBirth';
import TimeOfBirth from './pages/TimeOfBirth';
import OTPVerification from './pages/OTPVerification';
import LetsExplore from './pages/LetsExplore';
import StoryVideo from './pages/StoryVideo';
import UltraPremiumDashboard from './pages/UltraPremiumDashboard';
import UltraPremiumDashboardNew from './pages/UltraPremiumDashboardNew';
import DomainDetailNew from './pages/DomainDetailNew';
import DomainDetailComplete from './pages/DomainDetailComplete';
import MyStory from './pages/MyStory';
import MyStoryTab from './pages/MyStoryTab';
import Profile from './pages/Profile';
import ProfileTab from './pages/ProfileTab';
import KernelDisplay from './pages/KernelDisplay';
import KernelDisplayComplete from './pages/KernelDisplayComplete';
import MandeepKernelPage from './pages/MandeepKernelPage';
import IncorporationKernelPage from './pages/IncorporationKernelPage';
import DynamicKernelPage from './pages/DynamicKernelPage';
import KernelViewer from './pages/KernelViewer';
import ValidationDashboard from './pages/validation/ValidationDashboard';
import ValidationTestPage from './pages/validation/ValidationTestPage';
import BatchValidation from './pages/validation/BatchValidation';
import InteractiveLearning from './pages/validation/InteractiveLearning';
import RelationshipKernelViewer from './pages/RelationshipKernelViewer';
import LifeIntelligenceDashboard from './pages/LifeIntelligenceDashboard';
import TrillionDollarDashboard from './pages/TrillionDollarDashboard';
import DashboardTrillionDollar from './pages/DashboardTrillionDollar';
import RealHealthDashboard from './pages/RealHealthDashboard';
import UltimateDashboard from './pages/UltimateDashboard';
import PaymentPlans from './pages/PaymentPlans';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import ContactPage from './pages/ContactPage';
import PricingPage from './pages/PricingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import KalachakraDasa from './pages/KalachakraDasa';
import TaraanayaTest from './pages/TaraanayaTest';
import NuclearReset from './pages/NuclearReset';
import { Toaster } from '@/components/ui/sonner';
import FloatingContact from './components/FloatingContact';
import PurchaseVerifyingOverlay from './components/PurchaseVerifyingOverlay';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// AppRouter handles auth callback synchronously before rendering routes
function AppRouter() {
  const location = useLocation();
  
  // Global route change tracker — fires PostHog + Meta Pixel on every navigation
  useEffect(() => {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('$pageview', { $current_url: window.location.href });
    }
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);
  
  // Check URL fragment for session_id synchronously (NOT in useEffect)
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* New Redesigned Flow */}
      {/* App entry with splash screen for native app */}
      <Route path="/" element={<AppEntry />} />
      <Route path="/app" element={<AppEntry />} />
      <Route path="/splash-shine" element={<SplashFlawless />} />
      <Route path="/splash-classic" element={<SplashClassic />} />
      <Route path="/splash-bright" element={<SplashCinematic />} />
      <Route path="/splash-old" element={<SplashPerfect />} />
      <Route path="/splash-old" element={<SplashNew />} />
      <Route path="/onboarding" element={<Navigate to="/onboarding-new" replace />} />
      <Route path="/onboarding-new" element={<SmartNotebookInput />} />
      <Route path="/name-entry" element={<ProtectedRoute><NameEntryNew /></ProtectedRoute>} />
      <Route path="/date-of-birth" element={<ProtectedRoute><DateOfBirthNew /></ProtectedRoute>} />
      <Route path="/time-of-birth" element={<ProtectedRoute><TimeOfBirthNew /></ProtectedRoute>} />
      <Route path="/place-of-birth" element={<ProtectedRoute><PlaceOfBirthNew /></ProtectedRoute>} />
      <Route path="/otp-verification" element={<ProtectedRoute><OTPVerificationNew /></ProtectedRoute>} />
      <Route path="/loading-story" element={<ProtectedRoute><LoadingStory /></ProtectedRoute>} />
      <Route path="/loading-past-reels" element={<LoadingPastStoryReels />} />
      <Route path="/generating-story" element={<LoadingStoryGenerator />} />
      <Route path="/past-story" element={<FreeStoryGate><PastStoryNew /></FreeStoryGate>} />
      <Route path="/past-story-landing" element={<PastStoryLanding />} />
      <Route path="/watch" element={<WatchRouteResolver />} />
      {/* /watch1 is deprecated as part of the Web-First launch — redirect to /watch */}
      <Route path="/watch1" element={<Navigate to="/watch" replace />} />
      <Route path="/about" element={<WebsiteAboutPage />} />
      <Route path="/landing" element={<LandingPageMarketing />} />
      <Route path="/your-story" element={<FreeStoryGate><PastStoryRevolutionary /></FreeStoryGate>} />
      
      {/* Premium Routes - Require payment for access */}
      {/* Future Reels - Requires premium_599+ plan */}
      <Route path="/your-future-story" element={<FeatureRoute feature="future_reels"><FutureStoryRevolutionary /></FeatureRoute>} />
      <Route path="/future-story-detailed" element={<FeatureRoute feature="future_reels"><FutureStoryDetailed /></FeatureRoute>} />
      <Route path="/domain-story/:domainId" element={<FeatureRoute feature="future_reels"><FutureStoryExact /></FeatureRoute>} />
      <Route path="/loading-domain/:domain" element={<FeatureRoute feature="future_reels"><LoadingDomainReel /></FeatureRoute>} />
      <Route path="/future-reels-loading" element={<FeatureRoute feature="future_reels"><FutureReelsLoading /></FeatureRoute>} />
      <Route path="/domain-reels/:domainId" element={<FeatureRoute feature="future_reels"><DomainReelsStory /></FeatureRoute>} />
      <Route path="/future-story/:domainKey" element={<FeatureRoute feature="future_reels"><FutureStoryExact /></FeatureRoute>} />
      {/* Life Insights - Requires any premium plan (starter_199+) */}
      <Route path="/insights" element={<PremiumRoute><DomainInsights /></PremiumRoute>} />
      <Route path="/domain-insights" element={<PremiumRoute><DomainInsights /></PremiumRoute>} />
      
      {/* Protected Routes - Require login only */}
      <Route path="/your-story-cinematic" element={<ProtectedRoute><PastStoryUnifiedCinematic /></ProtectedRoute>} />
      <Route path="/your-story-animated" element={<PastStoryAnimated />} />
      <Route path="/your-story-old" element={<ProtectedRoute><PastStoryUnified /></ProtectedRoute>} />
      <Route path="/career-insights" element={<ProtectedRoute><CareerInsights /></ProtectedRoute>} />
      <Route path="/hex-story" element={<ProtectedRoute><HexStory /></ProtectedRoute>} />
      <Route path="/login" element={<LoginFirebase />} />
      <Route path="/payment-plans" element={<PaymentPlans />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/credits-refill" element={<ProtectedRoute><CreditsRefill /></ProtectedRoute>} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/home" element={<ProtectedRoute><BeautifulDashboard /></ProtectedRoute>} />
      <Route path="/home-gradient" element={<ProtectedRoute><HomeInnovative /></ProtectedRoute>} />
      <Route path="/home-minimal" element={<ProtectedRoute><HomeMinimal /></ProtectedRoute>} />
      <Route path="/home-old" element={<ProtectedRoute><HomeNew /></ProtectedRoute>} />
      <Route path="/health-minimal" element={<HealthMinimal />} />
      <Route path="/health-real" element={<RealHealthDashboard />} />
      <Route path="/ultimate" element={<UltimateDashboard />} />
      <Route path="/my-domains" element={<MyDomainsNew />} />
      <Route path="/domain/:domainId" element={<DomainDetailUniversal />} />
      <Route path="/ai-agents" element={<AIAgentsHub />} />
      {/* Timeline - Requires ultimate_999 plan */}
      <Route path="/dashboard-new" element={<FeatureRoute feature="timeline"><DashboardNew /></FeatureRoute>} />
      <Route path="/story-video" element={<StoryVideoNew />} />
      <Route path="/profile-new" element={<ProfileNew />} />
      <Route path="/validation" element={<ValidationResults />} />
      <Route path="/complete-validation" element={<CompleteValidation />} />
      <Route path="/100-aspects" element={<Complete100Analysis />} />
      <Route path="/algorithm-validation" element={<AlgorithmValidation />} />
      <Route path="/exact-prediction" element={<ExactPrediction />} />
      <Route path="/health-story" element={<HealthStoryFlow />} />
      <Route path="/bulk-kernels" element={<BulkKernelGenerator />} />
      <Route path="/complete-predictions" element={<CompletePredictions />} />
      <Route path="/mandeep-debug" element={<MandeepHealthDebug />} />
      <Route path="/audit-trail" element={<AuditTrailReport />} />
      <Route path="/marketing-content" element={<SocialMediaContent />} />
      <Route path="/celebrity-dataset" element={<CelebrityDatasetDownload />} />
      <Route path="/brand-materials" element={<BrandMaterials />} />
      
      {/* Old Routes (kept for compatibility) */}
      <Route path="/old-splash" element={<ZodiacSplash />} />
      <Route path="/name" element={<NameEntry />} />
      <Route path="/old-place-of-birth" element={<PlaceOfBirth />} />
      <Route path="/verify-otp" element={<OTPVerification />} />
      <Route path="/payment" element={<PaymentPlans />} />
      <Route path="/lets-explore" element={<LetsExplore />} />
      <Route path="/story-video" element={<StoryVideo />} />
      <Route path="/dashboard" element={<ProtectedRoute><BeautifulDashboard /></ProtectedRoute>} />
      <Route path="/dashboard-old" element={<ProtectedRoute><UltimateDashboard /></ProtectedRoute>} />
      <Route path="/domain/:domain" element={<DomainDetailComplete />} />
      <Route path="/my-story" element={<MyStory />} />
      <Route path="/my-story-tab" element={<MyStoryTab />} />
      <Route path="/profile" element={<ProfileTab />} />
      <Route path="/kernel" element={<KernelDisplayComplete />} />
      <Route path="/mandeep-kernel" element={<MandeepKernelPage />} />
      <Route path="/incorporation-kernel" element={<IncorporationKernelPage />} />
      <Route path="/dynamic-kernel" element={<DynamicKernelPage />} />
      <Route path="/kernel-viewer" element={<KernelViewer />} />
      <Route path="/validation" element={<ValidationDashboard />} />
      <Route path="/validation-test" element={<ValidationTestPage />} />
      <Route path="/validation-batch" element={<BatchValidation />} />
      <Route path="/validation-interactive" element={<InteractiveLearning />} />
      <Route path="/kernel-old" element={<KernelDisplay />} />
      <Route path="/kernel-relationship" element={<RelationshipKernelViewer />} />
      <Route path="/kernel-relationship/:userId" element={<RelationshipKernelViewer />} />
      <Route path="/life-intelligence" element={<LifeIntelligenceDashboard />} />
      <Route path="/trillion-dashboard" element={<TrillionDollarDashboard />} />
      <Route path="/kalachakra-dasa" element={<KalachakraDasa birthData={{date: '1997-05-14', time: '12:17:00', lat: 30.3255646, lon: 78.0436813}} />} />
      <Route path="/taraanaya-test" element={<TaraanayaTest />} />
      <Route path="/reset" element={<NuclearReset />} />
    </Routes>
  );
}

function App() {
  return (
    <SessionProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
          <FloatingContact />
          <PurchaseVerifyingOverlay />
          <PWAInstallPrompt />
        </BrowserRouter>
        <Toaster position="top-center" duration={3000} />
      </div>
    </SessionProvider>
  );
}

export default App;
